import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import includes from 'lodash/includes';
import get from 'lodash/get';
import find from 'lodash/find';

import { getCurrentPage } from 'reducers/ui';
import { loginStatus } from 'utils/constants/status';
import { assetStatus } from 'utils/constants/assetText';
import {
  getPersonnelRoles,
  getPersonnelUserId,
  getPersonnelDetailsRaw,
  getPersonnelListRawTransformed,
} from '../Selectors/personnelSelectors';
import styles from './index.module.scss';
import { getQualifiedAsset } from 'utils/assetHelper';
import OperationalAction from 'components/Operational/operationalAction';
import { serviceTypes } from 'utils/constants/serviceTypes';
import { getPatrols } from './helper';
import ShowAssignedAssets from './showAssignedAssets';
import { getTimezone } from 'reducers/utils';
import { getRegionId, getSecurityId, getUserRoles } from 'reducers/users';
import { transformPatrolReportObject } from 'utils/DataTransform/Patrols/Form/PatrolDefObject';
import { validationObject, validatePassCode } from 'utils/validations';
import { assetPageSpecificActions } from 'containers/Pages/Assets/Assets';
import { personnelPageActions } from 'containers/Pages/People/Personnel';
import { DEFAULT_EMPTY_VALUE } from 'utils/constants';
import Portal from 'portal/Portal';
import Button from 'components/Button';
import FormContainer from 'components/Forms';
import fieldSelection from 'components/Forms/Fields';
import Loader from 'components/Loader';
import API from 'services/api';
import { Box } from '@material-ui/core';
import {
  checkIfUserIsSimpleUser,
  SupervisorAndAbove,
  userHasRole,
} from 'utils/helper/roles';
import { transform } from 'utils/DataTransform/Asset/AssetList';
import { transform as patrolDefTransform } from 'utils/DataTransform/Patrols/PatrolDefList';
import staticServices from 'static/services.json';
import { cleaningDataGridActions } from 'containers/Pages/Tasks/CleanerTasks';
import { v4 } from 'uuid';
import { translateTwoValueSentence } from 'utils/methods/helpers';

export const validationSchema = validationObject({
  passcode: validatePassCode(true, 4, 6),
});

const inputFields = [
  {
    field: 'passcode',
    type: 'text',
    required: true,
    placeholder: 'Please enter 4-6 digit passcode',
  },
];

const OperationalComponent = ({
  personnelDetails,
  securityCompanyId,
  rawPersonnel,
  assignToPatrol,
  assetGiver,
  assetHandover,
  makeAssetUnavailable,
  currentPage,
  timezone,
  regionId,
  assignPersonnelToTask,
}) => {
  // state hooks
  const [selectedPatrol, setSelectedPatrol] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetHandoverPopup, setAssetHandoverPopup] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assetList, setAssetList] = useState([]);
  const [patrolDefList, setPatrolDefList] = useState([]);
  // selectors
  const userId = useSelector(state => getPersonnelUserId(state));
  const role = useSelector(state => getPersonnelRoles(state));
  const userRoles = useSelector(state => getUserRoles(state));

  const checkDisable = () => {
    /**
     * Enabled if status is AVAILABLE and service include PATROL_SINGLE_SITE/PATROL_MULTISITE
     */
    const hasSingleSiteService = includes(
      get(personnelDetails, 'details.services'),
      serviceTypes.patrol.singleSite,
    );
    const hasMultiSiteService = includes(
      get(personnelDetails, 'details.services'),
      serviceTypes.patrol.multiSite,
    );

    return !(
      (hasSingleSiteService || hasMultiSiteService) &&
      personnelDetails.operational.status === loginStatus.AVAILABLE
    );
  };

  const assignPatrol = () => {
    const myData = find(rawPersonnel, ['id', personnelDetails.general.id]);
    const desiredPatrolData = find(patrolDefList, {
      id: selectedPatrol.id,
    });
    const transformPatrol = transformPatrolReportObject(
      desiredPatrolData,
      regionId,
      timezone,
    );
    assignToPatrol(myData.value, transformPatrol);
    setSelectedPatrol(null);
  };
  const assignTask = () => {
    const { firstName } = personnelDetails.general;
    const { lastName } = personnelDetails.general;
    const assigneeId = personnelDetails.general.id;
    // add personnel info to task
    selectedTask['assigneeFullName'] = `${firstName} ${lastName}`;
    selectedTask['assigneeId'] = assigneeId;
    // make api request
    assignPersonnelToTask({
      id: selectedTask.id,
      data: { tasks: [selectedTask] },
    });
  };

  const checkAssetHandover = () => {
    const selectedAssetData = find(assetList, ['id', selectedAsset.id]);
    if (selectedAssetData.handoverRequired) {
      setAssetHandoverPopup(true);
    } else {
      assignAssetToPersonnel();
    }
  };
  const assignAssetToPersonnel = () => {
    Reflect.deleteProperty(assetGiver, 'location');
    assetHandover({
      id: selectedAsset.id,
      giver: assetGiver,
      receiver: personnelDetails,
      state: assetStatus.DEPLOYING,
      location: currentPage,
    });
    closeHandoverPopup();
  };
  const closeHandoverPopup = () => {
    setAssetHandoverPopup(false);
  };

  const markAssetUnavailable = () => {
    makeAssetUnavailable(selectedAsset.id);
    setAssetHandoverPopup(false);
  };

  // Reset Button component functions----------
  const updatePassword = async vals => {
    setLoading(true);
    const data = { password: vals.passcode }; //required as API has different data key
    try {
      await API.updatePasscode(userId, data);
      setOpenForm(false);
    } catch (err) {
      console.log('err', err);
    }
    setLoading(false);
  };
  //filter the tasks based on all the services where isTask is true
  const isTaskTrueServices = ({ personnelServices }) => {
    if (!personnelServices) return null;
    return staticServices
      .filter(f => personnelServices.includes(f.id))
      .filter(f => f.isTask)
      .map(m => m.category);
  };

  const resetButtonComponent = role =>
    checkIfUserIsSimpleUser(role) ? (
      <Box
        component={'div'}
        display={'flex'}
        alignItems={'flex-start'}
        paddingLeft={'1rem'}
        flexDirection={'column'}
      >
        <Button
          text="Reset Passcode"
          size="l"
          className={styles.otpButton}
          onClick={() => setOpenForm(true)}
        />

        {openForm &&
          userHasRole({
            userRoles: userRoles,
            requiredRoles: SupervisorAndAbove,
          }) && (
            <Portal id="portal">
              <div className={styles.relative}>
                <FormContainer
                  size="s"
                  title="Change Passcode"
                  requireHeaderSaveBtn
                  onClickClose={() => setOpenForm(false)}
                  onSubmit={updatePassword}
                  buttonText="Submit"
                  validationSchema={validationSchema}
                  initialValues={{}}
                >
                  {props => {
                    return inputFields.map((item, key) =>
                      fieldSelection({ item, key, props }),
                    );
                  }}
                </FormContainer>
                {loading && <Loader outline={true} />}
              </div>
            </Portal>
          )}
        {loading && <Loader outline={true} />}
      </Box>
    ) : null;
  //---------------------------------
  const getActionItem = type => {
    switch (type) {
      case 'assign-asset':
        return {
          label: 'Assign Asset',
          placeholder: 'Select Asset',
          selectedValue: selectedAsset,
          changeSelectedValue: setSelectedAsset,
          btnText: 'Assign',
          btnSize: 's',
          btnColor: 'primary',
          btnClick: checkAssetHandover,
          btnDisabled: !selectedAsset,
          fetchOptions: API.getAssetList,
          dataTransform: ({ data }) => {
            const transformedAssetList = transform(data);
            setAssetList(transformedAssetList);
            return getQualifiedAsset(transformedAssetList, personnelDetails);
          },
          fetchFilters: {
            securityCompanyId,
            archived: false,
          },
          optionLabelKey: 'label',
        };
      case 'assign-patrol':
        return {
          label: 'Assign Patrol',
          placeholder: 'select a patrol',
          selectedValue: selectedPatrol,
          changeSelectedValue: setSelectedPatrol,
          isDisabled: checkDisable(),
          btnText: 'Assign',
          btnSize: 's',
          btnClick: assignPatrol,
          disabled: !selectedPatrol || checkDisable(),
          btnDisabled: !selectedPatrol || checkDisable(),
          fetchOptions: API.fetchPatrolDefList,
          dataTransform: ({ data }) => {
            const transformedPatrolDefList = patrolDefTransform(data);

            setPatrolDefList(transformedPatrolDefList);
            return getPatrols(personnelDetails, transformedPatrolDefList);
          },
          fetchFilters: {
            securityCompanyId,
            archived: false,
          },
          optionLabelKey: 'label', // this key is used to destructure and show the label in the dropdown in the async component.
          //  If your data transform is returning a map like [{id:'87ah9',label:'fakeLabel'}], then this key should be set as 'label' otherwise 'name'
        };
      case 'assign-task':
        return {
          label: 'Assign task',
          placeholder: 'select a task',
          selectedValue: selectedTask,
          changeSelectedValue: setSelectedTask,
          btnText: 'Assign',
          btnSize: 's',
          btnClick: assignTask,
          btnDisabled: !selectedTask,
          fetchOptions: API.fetchTaskList,
          dataTransform: ({ data }) => {
            return data.map(m => {
              if (m.id === '') {
                // dummy tasks have no id, so assign an id
                return { ...m, id: v4() };
              }
              return m;
            });
          },
          fetchFilters: {
            securityCompanyId,
            archived: false,
            taskCategory: isTaskTrueServices({
              personnelServices: personnelDetails?.details?.services,
            }),
          },
          optionLabelKey: 'name', // this key is used to destructure and show the label in the dropdown in the async component.
          //  If your data transform is returning a map like [{id:'87ah9',label:'fakeLabel'}], then this key should be set as 'label' otherwise 'name'
        };
      default:
        return null;
    }
  };
  const getPopupData = type => {
    switch (type) {
      case 'assign-asset':
        return {
          showConfirmPopup: assetHandoverPopup,
          popupTitle: 'asset_handover_title',
          popupStatusMessage:
            selectedAsset !== null
              ? translateTwoValueSentence('asset_handover_desc', {
                  value1:
                    selectedAsset?.assetId &&
                    selectedAsset?.assetId !== DEFAULT_EMPTY_VALUE
                      ? selectedAsset.assetId
                      : '',
                  value2: selectedAsset.label,
                })
              : '',
          confirmBtnHandler: assignAssetToPersonnel,
          confirmBtnText: 'asset_handover_pos_button',
          cancelBtnHandler: markAssetUnavailable,
          cancelBtnText: 'asset_handover_neg_button',
          closePopupHandler: closeHandoverPopup,
        };
      default:
        return null;
    }
  };

  if (!personnelDetails) {
    return null;
  }

  const checkIfServicesHasIsTask = ({ personnelServices }) => {
    if (!personnelServices) return false;
    return staticServices
      .filter(f => personnelServices.includes(f.id))
      .some(s => s.isTask);
  };
  // If user has service which has isTasktrue then show Assign Task
  const serviceHasTaskIsTrue = checkIfServicesHasIsTask({
    personnelServices: personnelDetails?.details?.services,
  });

  return (
    <div className={styles.operationalPanel}>
      <OperationalAction
        type={'asyncDropdown'}
        item={getActionItem('assign-patrol')}
      />
      <OperationalAction
        type={'asyncDropdown'}
        item={getActionItem('assign-asset')}
        confirmPopup={getPopupData('assign-asset')}
      />
      <ShowAssignedAssets
        assignedAssets={personnelDetails.operational.assignedAssets}
        personnelId={personnelDetails.general.id}
        assignee={personnelDetails.general}
      />
      {serviceHasTaskIsTrue && (
        <OperationalAction
          type={'asyncDropdown'}
          item={getActionItem('assign-task')}
        />
      )}
      {resetButtonComponent(role)}
    </div>
  );
};

OperationalComponent.propTypes = {
  personnelDetails: PropTypes.object,
  rawPersonnel: PropTypes.array,
  assignToPatrol: PropTypes.func,
  assetGiver: PropTypes.object,
  assetHandover: PropTypes.func,
  makeAssetUnavailable: PropTypes.func,
  currentPage: PropTypes.string,
  securityCompanyId: PropTypes.string,
  regionId: PropTypes.string,
  timezone: PropTypes.string,
  toggleModal: PropTypes.func,
  assignPersonnelToTask: PropTypes.func,
  toggleFromMessaging: PropTypes.func,
  toggleEditMode: PropTypes.func,
};

const mapStateToProps = state => ({
  personnelDetails: getPersonnelDetailsRaw(state), // untransformed Details
  rawPersonnel: getPersonnelListRawTransformed(state), // transformed Raw List
  assetGiver: state.user,
  timezone: getTimezone(state),
  currentPage: getCurrentPage(state),
  regionId: getRegionId(state),
  securityCompanyId: getSecurityId(state),
});

const mapDispatchToProps = dispatch => ({
  assignToPatrol: (personnel, patrolDef) =>
    dispatch(personnelPageActions.assignToPatrol({ personnel, patrolDef })),
  assetHandover: ({ id, giver, receiver, state, location }) =>
    dispatch(
      assetPageSpecificActions.assetHandover({
        id,
        giver,
        receiver,
        state,
        location,
      }),
    ),
  makeAssetUnavailable: id =>
    dispatch(assetPageSpecificActions.makeAssetUnavailable(id)),
  assignPersonnelToTask: ({ id, data }) =>
    dispatch(cleaningDataGridActions.updateRowEntry({ id, data })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OperationalComponent);
